import React, { useEffect, useState, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const Services = ({ id = 'services' }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  // Video autoplay state and ref
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const videoRef = useRef(null);
  const [videoInView, setVideoInView] = useState(false);
  
  // Video intersection observer
  const [videoRef2, videoInViewObserver] = useInView({
    threshold: 0.5,
    triggerOnce: false
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  
  // Handle video autoplay on scroll
  useEffect(() => {
    if (videoInViewObserver && !isVideoPlaying) {
      setIsVideoPlaying(true);
      setVideoInView(true);
    } else if (!videoInViewObserver && isVideoPlaying) {
      setIsVideoPlaying(false);
      setVideoInView(false);
    }
  }, [videoInViewObserver, isVideoPlaying]);
  
  // Handle play button click
  const handlePlayClick = () => {
    setShowOverlay(false);
    setIsVideoPlaying(true);
    setVideoInView(true);
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const services = [
    {
      id: 'verification',
      title: 'Human Verification Services',
      description: 'Comprehensive identity and background verification through our network of trusted agents across Africa.',
      details: [
        { 
          icon: 'user', 
          text: 'Identity & Background Checks' 
        },
        { 
          icon: 'briefcase', 
          text: 'Employment & Education Verification' 
        },
        { 
          icon: 'home', 
          text: 'Address & Guarantor Verification' 
        }
      ],
      cta: {
        text: 'Request Verification',
        link: '/verification'
      }
    },
    {
      id: 'travel',
      title: 'Travel & Immigration Support',
      description: 'Expert guidance and document assistance for visa applications and immigration processes.',
      details: [
        { 
          icon: 'globe', 
          text: 'Visa Route Consultation' 
        },
        { 
          icon: 'document', 
          text: 'Document Preparation & Review' 
        },
        { 
          icon: 'coaching', 
          text: 'Interview Coaching & Support' 
        }
      ],
      cta: {
        text: 'Book Consultation',
        link: '/travel'
      }
    },
    {
      id: 'corporate',
      title: 'Corporate Due Diligence',
      description: 'Professional verification services for businesses, including financial standing and corporate background checks.',
      details: [
        { 
          icon: 'building', 
          text: 'Corporate Background Verification' 
        },
        // { 
        //   icon: 'dollar', 
        //   text: 'Financial Standing Attestation' 
        // },
        // { 
        //   icon: 'shield', 
        //   text: 'Compliance & Risk Assessment' 
        // }
      ],
      cta: {
        text: 'Learn More',
        link: '/corporate'
      }
    }
  ];

  const renderIcon = (icon) => {
    switch (icon) {
      case 'user':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
          </svg>
        );
      case 'briefcase':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
        );
      case 'home':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
          </svg>
        );
      case 'globe':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        );
      case 'document':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
        );
      case 'coaching':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"></path>
          </svg>
        );
      case 'building':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
          </svg>
        );
      case 'dollar':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        );
      case 'shield':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
          </svg>
        );
      default:
        return null;
    }
  };

  const renderServiceCard = (service, index) => {
    return (
      <motion.div
        key={service.id}
        variants={itemVariants}
        className="w-full md:w-1/3 px-4 mb-8 md:mb-0"
      >
        <div className="bg-white h-full rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col hover:shadow-md transition-shadow">
          <div className="p-6 flex-grow">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-acYellow bg-opacity-10 rounded-full flex items-center justify-center">
                <div className="w-8 h-8 bg-acYellow rounded-full"></div>
              </div>
            </div>
            
            <h3 className="text-xl font-inter font-bold text-acBlack mb-3 text-center">
              {service.title}
            </h3>
            
            <p className="text-gray-600 mb-6 text-center leading-relaxed">
              {service.description}
            </p>
            
            <div className="space-y-3 mb-6">
              {service.details.map((detail, i) => (
                <div key={i} className="flex items-center">
                  <div className="mr-3 flex-shrink-0">
                    {renderIcon(detail.icon)}
                  </div>
                  <span className="text-sm text-gray-700">{detail.text}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="px-6 pb-6">
            <Link 
              to={service.cta.link} 
              className="block w-full py-3 px-4 bg-white border border-acYellow text-acYellow text-center rounded-lg hover:bg-acYellow hover:text-white transition-colors duration-200 font-semibold"
            >
              {service.cta.text}
            </Link>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <section 
      id={id} 
      className="py-16 px-6 bg-white relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={containerVariants}
          className="text-left mb-12"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-4"
          >
            Our Services
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl text-gray-600 leading-relaxed"
          >
            Discover how Validate Systems provides comprehensive verification and travel assistance services 
            across Africa, ensuring trust and mobility through human-verified processes.
          </motion.p>
        </motion.div>
        
        {/* YouTube Video Section */}
        <motion.div 
          variants={itemVariants}
          className="mb-16 text-center"
        >
          <div 
            ref={videoRef2}
            className="max-w-5xl mx-auto relative rounded-lg overflow-hidden shadow-lg"
          >
            <div className="relative pb-[56.25%] h-0"> {/* 16:9 aspect ratio */}
              <iframe
                ref={videoRef}
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/DolJzPuCwZ0?${videoInView ? 'autoplay=1&' : ''}mute=1&rel=0&showinfo=0&modestbranding=1&playsinline=1`}
                title="Validate Systems Services Overview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
              
              {/* Overlay with Play Button */}
              {showOverlay && (
                <div className="absolute inset-0 bg-acBlack bg-opacity-30 flex items-center justify-center cursor-pointer transition-opacity duration-300 hover:bg-opacity-40"
                     onClick={handlePlayClick}>
                  <div className="relative">
                    {/* Play Button Background Circle */}
                    <div className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg hover:bg-opacity-100 transition-all duration-200 hover:scale-110">
                      {/* Play Icon */}
                      <svg 
                        className="w-8 h-8 text-acYellow ml-1" 
                        fill="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          variants={containerVariants}
          className="flex flex-wrap -mx-4"
        >
          {services.map(renderServiceCard)}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;